import { useState } from 'react';
import type { Action, Commande, PolitiqueTransfert, StatutCommande } from '../../api/types';
import { POLITIQUE_TRANSFERT_OPTION, STATUT_COMMANDE_OPTION } from '../../lib/labels';
import { fromLocalInput, toLocalInput } from '../../lib/format';
import Modal from '../Modal';
import { Button, Card, Field, Input, Select } from '../ui';

/**
 * Carte « Commande » : statut de commande + politique de transfert.
 * Les deux listes fonctionnent pareil : la sélection s'applique tout de suite. Quand un statut demande
 * plus (dates d'un report, confirmation d'une suppression côté app), une modale s'ouvre avant d'appliquer ;
 * l'annuler laisse la liste sur la valeur actuelle.
 */
export default function ReglagesCommande({
  commande,
  onAction,
  busy,
}: {
  commande: Commande;
  onAction: (action: Action) => void;
  busy: boolean;
}) {
  const [enAttente, setEnAttente] = useState<StatutCommande | null>(null);
  const [dateSeance, setDateSeance] = useState(toLocalInput(commande.dateSeance));
  const [dateInitiale, setDateInitiale] = useState(toLocalInput(commande.dateInitialeSeance ?? commande.dateSeance));

  const choisirStatut = (statut: StatutCommande) => {
    if (statut === commande.statut) return;
    if (statut === 0) {
      onAction({ action: 'setOrderStatus', body: { statut } });
      return;
    }
    setDateSeance(toLocalInput(commande.dateSeance));
    setDateInitiale(toLocalInput(commande.dateInitialeSeance ?? commande.dateSeance));
    setEnAttente(statut);
  };

  const appliquerStatut = () => {
    if (enAttente === null) return;
    const body: Extract<Action, { action: 'setOrderStatus' }>['body'] = { statut: enAttente };
    if (enAttente === 1) {
      const nextSeance = fromLocalInput(dateSeance);
      const initiale = fromLocalInput(dateInitiale);
      if (nextSeance) body.dateSeance = nextSeance;
      if (initiale) body.dateInitialeSeance = initiale;
    }
    onAction({ action: 'setOrderStatus', body });
    setEnAttente(null);
  };

  const destructif = enAttente === 2 || enAttente === 3;

  return (
    <Card>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-outline">Commande</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Statut de commande">
          <Select
            value={commande.statut}
            disabled={busy}
            onChange={(e) => choisirStatut(Number(e.target.value) as StatutCommande)}
          >
            {Object.entries(STATUT_COMMANDE_OPTION).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Politique de transfert">
          <Select
            value={commande.transfert}
            disabled={busy}
            onChange={(e) =>
              onAction({ action: 'setTransferPolicy', body: { transfert: Number(e.target.value) as PolitiqueTransfert } })
            }
          >
            {Object.entries(POLITIQUE_TRANSFERT_OPTION).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <p className="mt-2 text-xs/4 text-outline">Les changements s’appliquent dès la sélection.</p>

      {enAttente !== null ? (
        <Modal
          open
          title={enAttente === 1 ? 'Reporter la séance' : enAttente === 2 ? 'Annuler la commande' : 'Revente totale'}
          onClose={() => setEnAttente(null)}
          footer={
            <>
              <Button size="sm" variant="ghost" onClick={() => setEnAttente(null)}>
                Annuler
              </Button>
              <Button
                size="sm"
                variant={destructif ? 'danger' : 'primary'}
                disabled={busy || (enAttente === 1 && (!fromLocalInput(dateSeance) || !fromLocalInput(dateInitiale)))}
                onClick={appliquerStatut}
              >
                Appliquer
              </Button>
            </>
          }
        >
          {enAttente === 1 ? (
            <div className="space-y-3">
              <p className="text-sm text-on-surface-variant">
                <span className="font-semibold text-white">Dans l’app :</span> dialogue « séance reportée » avec
                l’ancienne puis la nouvelle date, affiché une seule fois.
              </p>
              <Field label="Nouvelle date de séance">
                <Input type="datetime-local" value={dateSeance} onChange={(e) => setDateSeance(e.target.value)} />
              </Field>
              <Field label="Date initiale" hint="Obligatoire pour une séance reportée (guide §8.2)">
                <Input type="datetime-local" value={dateInitiale} onChange={(e) => setDateInitiale(e.target.value)} />
              </Field>
            </div>
          ) : (
            <p className="text-sm text-on-surface-variant">
              <span className="font-semibold text-white">Dans l’app :</span> dialogue bloquant, puis l’app{' '}
              <span className="font-semibold text-error-container">supprime la commande</span> en local. Il faudra
              régénérer un lien d’import pour la réimporter.
            </p>
          )}
        </Modal>
      ) : null}
    </Card>
  );
}
